// Reconstruct line breaks from text pasted out of Word / PDF / HTML sources.
//
// Word docs and PDFs often flatten paragraph breaks and bullets when the
// clipboard is plain-text, producing walls of text like:
//   "Key Responsibilities Customer Support: Answer questions..."
//
// This helper re-inserts newlines using a few conservative heuristics:
//   1. Convert HTML tag artifacts (<br>, <p>, <li>) into newlines.
//   2. Bullet characters (•, ·, ▪, ◦, ●, ▶, ►) → newline + "• "
//   3. Numbered items ("1. ", "2) ") at start of segment → newline
//   4. Common JD section headers when they appear mid-flow (e.g.
//      "... needs Key Responsibilities Customer ..." → "…needs\n\nKey Responsibilities\nCustomer …").
//   5. Sentence + capitalized `Title:` mid-flow → newline before Title:
//
// The heuristics stay conservative — we only split where the source almost
// certainly meant a break; we never split arbitrary sentences.

const SECTION_HEADERS = [
    "Key Responsibilities",
    "Responsibilities",
    "Requirements",
    "Qualifications",
    "Required Skills",
    "Skills Required",
    "Preferred Skills",
    "Nice to have",
    "Nice-to-have",
    "Good to have",
    "Good-to-have",
    "Must have",
    "Must-have",
    "About the role",
    "About the Role",
    "About the company",
    "About the Company",
    "About us",
    "About Us",
    "Job Description",
    "Job Summary",
    "Overview",
    "Experience",
    "Education",
    "Perks",
    "Benefits",
    "What you'll do",
    "What you will do",
    "What we're looking for",
    "What we are looking for",
    "Work Experience",
    "Employment History",
    "Projects",
    "Certifications",
    "Awards",
    "Summary",
    "Objective",
    "Contact",
];

function _stripHtml(text) {
    // Convert block-level tags to newlines before stripping, so the visible
    // structure survives even for HTML pastes that hit the plain-text path.
    return text
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n")
        .replace(/<li[^>]*>/gi, "\n• ")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'");
}

/**
 * Reconstruct line-breaks from a pasted blob so JDs / resumes look properly
 * formatted in the form + on display. Returns a cleaned string with `\n`
 * separators — safe to shove into a `<textarea>` value.
 */
export function reformatPastedText(raw) {
    if (!raw) return "";
    // V1.5.8.18 — defensive: an upstream fetch/response quirk was writing
    // objects into the CV textarea on Prod, producing "[object Object]".
    // Non-string input is now discarded rather than String()-coerced.
    if (typeof raw !== "string") return "";

    // 1) Strip and unescape any HTML we accidentally caught.
    let s = _stripHtml(raw);

    // 2) Normalize CR/LF into LF; convert non-breaking space into space.
    s = s.replace(/\r\n?/g, "\n").replace(/\u00A0/g, " ");

    // 3) Bullet characters — replace each with `\n• `.
    s = s.replace(/[\u2022\u25CF\u25AA\u25CB\u25E6\u25B6\u25BA\u00B7]/g, "\n• ");

    // 4) Numbered list markers at the start of a phrase — insert newline
    //    when we see "1. Word" or "2) Word" mid-flow.
    s = s.replace(/(?<=[A-Za-z\)])\s+(\d{1,2}[.\)])\s+([A-Z])/g, "\n$1 $2");

    // 5) Insert paragraph break before known section headers when they appear
    //    mid-flow. We build a single alternation regex sorted by length
    //    descending so "Key Responsibilities" wins over "Responsibilities"
    //    and "Work Experience" wins over "Experience" (otherwise the
    //    shorter header splits the middle of the longer one).
    const sortedHeaders = [...SECTION_HEADERS].sort((a, b) => b.length - a.length);
    const headersAlt = sortedHeaders.map(escapeRegex).join("|");
    s = s.replace(
        new RegExp(
            `(?<=[a-z0-9\\.\\!\\?\\)\\"'])\\s+(${headersAlt})(?=\\s|:|-)`,
            "g",
        ),
        "\n\n$1",
    );

    // 5b) When a section header sits immediately before an inline sub-header
    //    like "Key Responsibilities Customer Support: …", split between them
    //    so the section title gets its own line.
    s = s.replace(
        new RegExp(
            `(^|\\n)(${headersAlt})\\s+(?=[A-Z][A-Za-z0-9&/\\- ]{2,40}?:)`,
            "g",
        ),
        "$1$2\n",
    );

    // 6) Split a `Sentence.  Title Case Word: content` pattern into two lines
    //    when the second segment looks like an inline sub-header. We use a
    //    non-greedy match so multi-word section titles (e.g. "Key Responsibilities")
    //    don't get eaten into a single sub-header capture, and we allow common
    //    punctuation (hyphens/ampersands) that appear in things like
    //    "Cross-selling" and "Sales & Cross-selling".
    s = s.replace(
        /([\.!?])\s+([A-Z][A-Za-z0-9&/\- ]{2,40}?):\s+/g,
        "$1\n$2: ",
    );

    // 7) Collapse runs of spaces/tabs (never touch newlines).
    s = s.replace(/[ \t]{2,}/g, " ");

    // 8) Collapse 3+ blank lines to at most 2 (keeps paragraphs tidy).
    s = s.replace(/\n{3,}/g, "\n\n");

    // 9) Trim per-line whitespace and drop leading blank lines.
    s = s
        .split("\n")
        .map((line) => line.replace(/[ \t]+$/g, "").replace(/^[ \t]+/, ""))
        .join("\n")
        .replace(/^\n+/, "")
        .replace(/\n+$/, "");

    return s;
}

function escapeRegex(v) {
    return v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Drop-in React `onPaste` handler that reformats the pasted text and
 * inserts it at the current caret. Preserves any existing text on either
 * side of the selection.
 */
export function smartPasteHandler(setValue) {
    return (event) => {
        try {
            const html = event.clipboardData?.getData("text/html") || "";
            const text = event.clipboardData?.getData("text/plain") || "";
            const raw = html || text;
            if (!raw) return; // let the browser handle it
            event.preventDefault();
            const cleaned = reformatPastedText(raw);

            const el = event.target;
            const start = el.selectionStart ?? el.value.length;
            const end = el.selectionEnd ?? el.value.length;
            const before = el.value.slice(0, start);
            const after = el.value.slice(end);
            const next = before + cleaned + after;
            setValue(next);
            // Restore caret just after the inserted block on next tick.
            requestAnimationFrame(() => {
                const pos = start + cleaned.length;
                try {
                    el.setSelectionRange(pos, pos);
                } catch (_) { /* readonly? ignore */ }
            });
        } catch (_) {
            /* fall through — browser default paste keeps working */
        }
    };
}
